import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import MapView from '../components/MapView';
import { Property } from '../types';
import { getPropertyById } from '../services/propertyService';

const PropertyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [property, setProperty] = useState<Property | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Fetch property data from Firebase
  useEffect(() => {
    const fetchProperty = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const propertyData = await getPropertyById(id);
        
        if (propertyData) {
          setProperty(propertyData);
          setError(null);
        } else {
          setError("Property not found");
        }
      } catch (err) {
        console.error("Error fetching property:", err);
        setError("Failed to load property details.");
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [id]);
  
  // If property is not found, show a message
  if (error || (!loading && !property)) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <Navbar />
        <div className="container-custom mx-auto py-12 px-4">
          <div className="bg-white rounded-lg p-8 text-center shadow-md">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Property Not Found</h2>
            <p className="text-gray-600 mb-6">The property you are looking for does not exist or has been removed.</p>
            <Link to="/" className="btn btn-primary">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  // Show loading state
  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <Navbar />
        <div className="container-custom mx-auto py-12 px-4 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }
  
  // Destructure property details for easier access once we're sure it exists
  const { 
    title, description, price, location, bedrooms, 
    bathrooms, area, type, images, features, coordinates
  } = property as Property;
  
  // Function to handle image navigation
  const handleImageNavigation = (index: number) => {
    setActiveImageIndex(index);
  };

  // Split description into paragraphs
  const descriptionParagraphs = description.split('\n\n');
  
  return (
    <div className="bg-gray-50 min-h-screen">
      <Navbar />
      
      {/* Breadcrumb Navigation */}
      <div className="bg-white shadow-sm">
        <div className="container-custom mx-auto py-4 px-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="inline-flex items-center space-x-1 md:space-x-3">
              <li className="inline-flex items-center">
                <Link to="/" className="text-gray-700 hover:text-primary">
                  Home
                </Link>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <Link to="/properties" className="ml-1 text-gray-700 hover:text-primary md:ml-2">
                    Properties
                  </Link>
                </div>
              </li>
              <li aria-current="page">
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-1 text-gray-500 md:ml-2">{title}</span>
                </div>
              </li>
            </ol>
          </nav>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container-custom mx-auto py-8 px-4">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column - Images and Details */}
          <div className="lg:w-2/3">
            {/* Image Gallery */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
              <div className="relative h-96">
                <img 
                  src={images[activeImageIndex]} 
                  alt={`${title} - Image ${activeImageIndex + 1}`}
                  className="w-full h-full object-cover" 
                />
                
                {/* Image Navigation Arrows */}
                {images.length > 1 && (
                  <>
                    <button 
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-2 hover:bg-white transition-colors"
                      onClick={() => handleImageNavigation((activeImageIndex - 1 + images.length) % images.length)}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button 
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-2 hover:bg-white transition-colors"
                      onClick={() => handleImageNavigation((activeImageIndex + 1) % images.length)}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}
              </div>
              
              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="flex p-4 gap-2 overflow-x-auto">
                  {images.map((image, index) => (
                    <img 
                      key={index}
                      src={image} 
                      alt={`Thumbnail ${index + 1}`}
                      className={`w-20 h-14 object-cover cursor-pointer rounded ${activeImageIndex === index ? 'ring-2 ring-primary' : 'opacity-70 hover:opacity-100'}`}
                      onClick={() => handleImageNavigation(index)}
                    />
                  ))}
                </div>
              )}
            </div>
            
            {/* Property Description */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Description</h2>
              
              {descriptionParagraphs.map((paragraph, index) => (
                <p key={index} className="text-gray-600 mb-4 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
            
            {/* Property Location Map */}
            {coordinates && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Location</h2>
                <p className="text-gray-600 mb-4">
                  <span className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {location}
                  </span>
                </p>
                <div className="h-96 rounded-lg overflow-hidden">
                  <MapView 
                    properties={[property as Property]}
                    center={[coordinates.lat, coordinates.lng]}
                    zoom={15}
                    height="100%"
                    selectedProperty={id}
                  />
                </div>
              </div>
            )}
            
            {/* Property Features */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Features</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <svg className="w-5 h-5 text-primary mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Right Column - Info and Contact */}
          <div className="lg:w-1/3">
            {/* Property Summary Card */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 sticky top-4">
              <h1 className="text-2xl font-bold text-gray-800 mb-2">{title}</h1>
              
              <p className="text-gray-600 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {location}
              </p>
              
              <div className="flex items-center justify-between py-4 border-t border-b border-gray-200 mb-6">
                <div className="text-center">
                  <span className="block text-lg font-semibold text-gray-800">{bedrooms}</span>
                  <span className="text-sm text-gray-600">Bedrooms</span>
                </div>
                <div className="text-center">
                  <span className="block text-lg font-semibold text-gray-800">{bathrooms}</span>
                  <span className="text-sm text-gray-600">Bathrooms</span>
                </div>
                <div className="text-center">
                  <span className="block text-lg font-semibold text-gray-800">{area}</span>
                  <span className="text-sm text-gray-600">m²</span>
                </div>
                <div className="text-center">
                  <span className="block text-lg font-semibold text-gray-800">{type}</span>
                  <span className="text-sm text-gray-600">Type</span>
                </div>
              </div>
              
              <div className="mb-6">
                <h3 className="text-3xl font-bold text-primary mb-2">
                  ${price.toLocaleString()}
                </h3>
                <p className="text-gray-600 text-sm">
                  Estimated payment: <span className="font-semibold">${Math.round(price / 360).toLocaleString()}/month</span>
                </p>
              </div>
              
              {/* Contact Form Toggle */}
              <button 
                className="btn btn-primary w-full mb-4"
                onClick={() => setShowContactForm(!showContactForm)}
              >
                Contact Agent
              </button>
              
              <a 
                href={`tel:+50622223333`} 
                className="flex items-center justify-center w-full py-2 px-4 border border-primary text-primary rounded-md hover:bg-primary/10 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Call Agent
              </a>
            </div>
            
            {/* Contact Form */}
            {showContactForm && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Interested in this property?</h3>
                <form>
                  <div className="mb-4">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                      placeholder="Your Name"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                      placeholder="Your Email"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                      placeholder="Your Phone"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Message
                    </label>
                    <textarea
                      id="message"
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                      placeholder="I'm interested in this property..."
                      defaultValue={`I'm interested in ${title} (ID: ${id}).`}
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full btn btn-primary"
                  >
                    Send Message
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailPage;